import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ChevronDown, ChevronLeft, ChevronUp, Send, Square } from 'lucide-react';
import {
  MAX_PROMPT_LENGTH,
  type PermissionRequest,
  type SessionEventType,
  type SessionStatus,
} from '@claude-remote/shared';
import { Button } from '../components/Button.js';
import { StatusBadge } from '../components/Badge.js';
import { Markdown } from '../components/Markdown.js';
import { ToolInputView } from '../components/ToolInputView.js';
import { AskUserQuestionCard } from '../components/AskUserQuestionCard.js';
import { ImageUploader, type UploadedImage } from '../components/ImageUploader.js';
import { toast } from '../components/Toast.js';
import { api, formatApiError } from '../lib/api.js';
import { ws } from '../lib/ws.js';
import { buildTitle, useDocumentTitle } from '../lib/use-document-title.js';

interface SessionRow {
  id: string;
  project_id: string;
  project_name: string;
  claude_session_id: string | null;
  title: string | null;
  status: SessionStatus;
  started_at: number;
  ended_at: number | null;
  cost_usd: number;
  input_tokens: number;
  output_tokens: number;
  cache_creation_input_tokens: number;
  cache_read_input_tokens: number;
}

interface EventRow {
  id: string;
  session_id: string;
  seq: number;
  type: SessionEventType;
  content: string;
  created_at: number;
}

interface PermissionRow {
  id: string;
  session_id: string;
  tool_name: string;
  tool_input: string;
  status: string;
  decided_by_device_id: string | null;
  decision_reason: string | null;
  created_at: number;
  decided_at: number | null;
  timeout_at: number | null;
  project_name: string;
  session_title: string | null;
}

function rowToRequest(row: PermissionRow): PermissionRequest {
  return {
    id: row.id,
    sessionId: row.session_id,
    toolName: row.tool_name,
    toolInput: row.tool_input,
    status: row.status as PermissionRequest['status'],
    decidedByDeviceId: row.decided_by_device_id,
    decisionReason: row.decision_reason,
    createdAt: row.created_at,
    decidedAt: row.decided_at,
    timeoutAt: row.timeout_at,
    projectName: row.project_name,
    sessionTitle: row.session_title,
  };
}

/** Format token count: 1234 → "1.2k", 1234567 → "1.2M", etc. */
function formatTokens(n: number): string {
  if (n < 1000) return String(n);
  if (n < 1_000_000) return (n / 1000).toFixed(1).replace(/\.0$/, '') + 'k';
  return (n / 1_000_000).toFixed(2).replace(/\.00$/, '') + 'M';
}

const ONLY_MINE_KEY = 'session-chat:only-mine';
const PAGE_LIMIT = 250;

export function SessionScreen() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const [session, setSession] = useState<SessionRow | null>(null);
  const [events, setEvents] = useState<EventRow[]>([]);
  const [permissions, setPermissions] = useState<PermissionRequest[]>([]);
  const [prompt, setPrompt] = useState('');
  const [images, setImages] = useState<UploadedImage[]>([]);
  const [sending, setSending] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [hasMoreOlder, setHasMoreOlder] = useState(false);
  // Тумблер «Только мои сообщения». Сохраняем выбор в localStorage —
  // фильтр держится между переходами по сессиям и перезагрузками PWA.
  const [onlyMine, setOnlyMine] = useState<boolean>(() => {
    try {
      return localStorage.getItem(ONLY_MINE_KEY) === '1';
    } catch {
      return false;
    }
  });
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const promptRef = useRef<HTMLTextAreaElement | null>(null);
  const inputAreaRef = useRef<HTMLDivElement | null>(null);
  // Ref на onlyMine для WS-handler'а: он живёт в useEffect с deps [sessionId]
  // и сам по себе не пересоздаётся при переключении тумблера. Без ref
  // фильтр срабатывал бы по «застарелому» значению.
  const onlyMineRef = useRef(onlyMine);
  useEffect(() => {
    onlyMineRef.current = onlyMine;
  }, [onlyMine]);

  // Шаблон «Проект — кратко название задачи». Если title ещё не пришёл
  // (или пустой) — отдаём «Сессия», чтобы не оставлять заголовок
  // предыдущего экрана.
  useDocumentTitle(buildTitle(session?.project_name, session?.title ?? 'Сессия'));

  const eventsUrl = useCallback(
    (extra?: string) => {
      const base = `/api/v1/sessions/${sessionId}/events`;
      const params = new URLSearchParams();
      if (onlyMine) params.set('only_user', '1');
      if (extra) {
        // extra — уже готовая query без `?` (например, `before_seq=42`).
        for (const [k, v] of new URLSearchParams(extra)) params.set(k, v);
      }
      const qs = params.toString();
      return qs ? `${base}?${qs}` : base;
    },
    [sessionId, onlyMine],
  );

  const load = useCallback(async () => {
    if (!sessionId) return;
    // Без after_seq/before_seq бэк отдаёт последние PAGE_LIMIT событий —
    // длинная сессия (1000+) не обрезается с конца. Если в ответе ровно
    // PAGE_LIMIT и самый старый seq > 0, значит выше есть ещё — показываем
    // кнопку «Загрузить старее». При фильтре «только мои» бэк сам отдаёт
    // отфильтрованные страницы по тому же seq-курсору.
    //
    // Pending permissions фетчим явно: если permission_request пришёл до
    // того, как пользователь открыл /session, WS-событие уже прошло и
    // подписка ничего не подхватит. Фильтруем общий список по sessionId
    // и конвертим snake_case → camelCase под PermissionRequest.
    const [s, e, perms] = await Promise.all([
      api.get<SessionRow>(`/api/v1/sessions/${sessionId}`),
      api.get<EventRow[]>(eventsUrl()),
      api.get<PermissionRow[]>(`/api/v1/permissions`),
    ]);
    setSession(s);
    setEvents(e);
    setPermissions(
      perms.filter((p) => p.session_id === sessionId).map(rowToRequest),
    );
    setHasMoreOlder(e.length >= PAGE_LIMIT && (e[0]?.seq ?? 0) > 0);
    scrollToTop();
  }, [sessionId, eventsUrl]);

  const loadOlder = useCallback(async () => {
    if (!sessionId || loadingOlder || events.length === 0) return;
    const oldest = events[0]!.seq;
    if (oldest <= 0) {
      setHasMoreOlder(false);
      return;
    }
    setLoadingOlder(true);
    try {
      const older = await api.get<EventRow[]>(eventsUrl(`before_seq=${oldest}`));
      setEvents((prev) => {
        // Гарантированно без дублей по seq.
        const seen = new Set(prev.map((x) => x.seq));
        const merged = [...older.filter((x) => !seen.has(x.seq)), ...prev];
        return merged.sort((a, b) => a.seq - b.seq);
      });
      setHasMoreOlder(older.length >= PAGE_LIMIT && (older[0]?.seq ?? 0) > 0);
    } finally {
      setLoadingOlder(false);
    }
  }, [sessionId, loadingOlder, events, eventsUrl]);

  useEffect(() => {
    void load();
  }, [load]);

  // Переключение тумблера: сохраняем выбор и перезагружаем ленту с нуля.
  // Не пытаемся «дофильтровать» уже загруженный массив — иначе ломается
  // пагинация (после toggle кнопка «старее» потащит не те страницы).
  function toggleOnlyMine() {
    setOnlyMine((v) => {
      const next = !v;
      try {
        localStorage.setItem(ONLY_MINE_KEY, next ? '1' : '0');
      } catch {
        /* private mode / quota — просто игнорим */
      }
      return next;
    });
  }

  useEffect(() => {
    if (!sessionId) return;
    ws.subscribe([sessionId]);
    const unsub = ws.onMessage((msg) => {
      if (msg.type === 'session_event' && msg.sessionId === sessionId) {
        // При активном фильтре «только мои» в ленту попадают только
        // user_prompt'ы. Permissions/status-сообщения обрабатываются ниже
        // независимо от фильтра — это служебные элементы, не контент чата.
        if (onlyMineRef.current && msg.eventType !== 'user_prompt') return;
        setEvents((prev) => {
          if (prev.some((x) => x.seq === msg.seq)) return prev;
          const event: EventRow = {
            id: `live-${msg.seq}`,
            session_id: msg.sessionId,
            seq: msg.seq,
            type: msg.eventType,
            content: msg.content,
            created_at: Date.now(),
          };
          // Реальное событие пришло — оптимистичные user_prompt'ы (если есть)
          // дублировать ни к чему.
          const cleaned =
            msg.eventType === 'user_prompt'
              ? prev.filter((x) => !x.id.startsWith('optimistic-'))
              : prev;
          return [...cleaned, event].sort((a, b) => a.seq - b.seq);
        });
        scrollToTop();
      } else if (msg.type === 'session_status' && msg.sessionId === sessionId) {
        setSession((prev) => (prev ? { ...prev, status: msg.status } : prev));
      } else if (msg.type === 'session_usage' && msg.sessionId === sessionId) {
        // Real-time cumulative usage update.
        setSession((prev) =>
          prev
            ? {
                ...prev,
                cost_usd: msg.costUsd,
                input_tokens: msg.inputTokens,
                output_tokens: msg.outputTokens,
                cache_creation_input_tokens: msg.cacheCreationInputTokens,
                cache_read_input_tokens: msg.cacheReadInputTokens,
              }
            : prev,
        );
      } else if (msg.type === 'permission_request' && msg.request.sessionId === sessionId) {
        setPermissions((prev) => [...prev, msg.request]);
      } else if (msg.type === 'permission_resolved') {
        setPermissions((prev) => prev.filter((p) => p.id !== msg.requestId));
      }
    });
    return () => {
      ws.unsubscribe([sessionId]);
      unsub();
    };
  }, [sessionId]);

  // Автогроу textarea промпта: при многострочном вводе поле расширяется
  // до max-h-40 (160px), дальше включается обычный textarea-скролл.
  // Сбрасываем height в auto, чтобы scrollHeight корректно уменьшался
  // при удалении строк.
  useEffect(() => {
    const el = promptRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [prompt]);

  // document.title с именем проекта — у пользователя одновременно открыто
  // несколько вкладок с похожими сессиями, и без проекта в заголовке
  // вкладки они неразличимы.
  useEffect(() => {
    if (!session) return;
    const project = session.project_name ?? '';
    const title = session.title ?? 'Сессия';
    const prev = document.title;
    document.title = project ? `${project} — ${title}` : title;
    return () => {
      document.title = prev;
    };
  }, [session?.project_name, session?.title]);

  // Лента отображается в обратном порядке: свежее — сверху. После
  // нового события возвращаемся к началу контейнера.
  function scrollToTop() {
    queueMicrotask(() => {
      const el = scrollRef.current;
      if (!el) return;
      el.scrollTop = 0;
    });
  }

  async function sendPrompt() {
    if (!sessionId || !prompt.trim() || !session) return;
    const text = prompt.trim();
    if (text.length > MAX_PROMPT_LENGTH) {
      toast.error(
        `Промпт слишком длинный: ${text.length.toLocaleString('ru-RU')} из ${MAX_PROMPT_LENGTH.toLocaleString('ru-RU')} символов`,
      );
      return;
    }
    const attachmentIds = images.map((img) => ({ id: img.id }));
    const isFinalSession = session.status === 'finished' || session.status === 'error';
    const canResume = isFinalSession && !!session.claude_session_id;
    setSending(true);
    // Оптимистично добавляем своё сообщение в ленту. seq ставим максимальный
    // — чтобы в reverse-display оптимистик попал в самый верх (свежее всех).
    const optimistic: EventRow = {
      id: `optimistic-${Date.now()}`,
      session_id: sessionId,
      seq: Number.MAX_SAFE_INTEGER - Date.now(),
      type: 'user_prompt',
      content: JSON.stringify({
        type: 'user',
        message: { role: 'user', content: text },
      }),
      created_at: Date.now(),
    };
    setEvents((prev) => [...prev, optimistic].sort((a, b) => a.seq - b.seq));
    setPrompt('');
    setImages([]); // Очищаем превью после отправки.
    scrollToTop();
    try {
      const body = attachmentIds.length > 0
        ? { prompt: text, attachments: attachmentIds }
        : { prompt: text };
      if (canResume) {
        await api.post(`/api/v1/sessions/${sessionId}/resume`, body);
      } else {
        await api.post(`/api/v1/sessions/${sessionId}/prompts`, body);
      }
    } catch (err) {
      // Не убираем оптимистичное сообщение — пользователь должен видеть
      // что он написал, даже если запрос упал. Меняем id на `failed-*`,
      // чтобы UI отрисовал маркер «не доставлено».
      setEvents((prev) =>
        prev.map((x) =>
          x.id === optimistic.id
            ? { ...x, id: `failed-${optimistic.id}` }
            : x,
        ),
      );
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  }

  async function interrupt() {
    if (!sessionId) return;
    await api.post(`/api/v1/sessions/${sessionId}/interrupt`);
  }

  async function forceFinish() {
    if (!sessionId) return;
    await api.post(`/api/v1/sessions/${sessionId}/force-finish`);
  }

  async function decide(id: string, decision: 'approve' | 'deny') {
    await api.post(`/api/v1/permissions/${id}/${decision}`);
  }

  async function answer(id: string, text: string) {
    await api.post(`/api/v1/permissions/${id}/approve`, { answer: text });
  }

  const isFinal = session?.status === 'finished' || session?.status === 'error';
  const canResume = isFinal && !!session?.claude_session_id;
  // Можем отправить новый промпт если сессия живая или возобновляемая.
  const canSend = !!session && (
    session.status === 'idle' ||
    session.status === 'running' ||
    canResume
  );
  const isRunning = session?.status === 'running' || session?.status === 'waiting_permission';
  // «Завершить» доступно для любой живой сессии — running/waiting/idle.
  // idle = ход закончен, но сессия в памяти агента и можно слать новый
  // промпт; пользователю иногда нужно закрыть её, не отправляя ничего.
  // «Завершить» доступно для любой не-finished сессии — включая error.
  // Для error это «отметить просмотренным»: переводит в finished, чтобы
  // пропала из списка Активных. Бэк форс-финиша для error не interrupt'ит
  // агента (там уже нечего interrupt'ить), просто меняет статус.
  const canForceFinish = !!session && session.status !== 'finished';
  const placeholder =
    session?.status === 'waiting_permission'
      ? 'Ждём разрешения…'
      : canResume
        ? 'Продолжить эту сессию…'
        : isFinal
          ? 'Сессия завершена'
          : 'Следующий промпт…';
  const backHref = session ? `/projects/${session.project_id}` : '..';
  const [titleExpanded, setTitleExpanded] = useState(false);
  // Двухшаговое подтверждение завершения вместо window.confirm():
  // в установленной PWA на iOS нативный confirm в standalone-режиме
  // нередко молча возвращает false на повторных экранах сессии до
  // полной перезагрузки страницы — кнопка «не работает». Инлайн-стейт
  // от этого свободен.
  const [finishArmed, setFinishArmed] = useState(false);
  const finishTimerRef = useRef<number | null>(null);
  function armFinish() {
    if (finishTimerRef.current !== null) {
      window.clearTimeout(finishTimerRef.current);
    }
    setFinishArmed(true);
    finishTimerRef.current = window.setTimeout(() => {
      setFinishArmed(false);
      finishTimerRef.current = null;
    }, 4000);
  }
  function confirmFinish() {
    if (finishTimerRef.current !== null) {
      window.clearTimeout(finishTimerRef.current);
      finishTimerRef.current = null;
    }
    setFinishArmed(false);
    void forceFinish();
  }
  useEffect(() => {
    return () => {
      if (finishTimerRef.current !== null) {
        window.clearTimeout(finishTimerRef.current);
      }
    };
  }, []);
  const title = session?.title ?? '…';
  const titleIsLong = title.length > 60;

  return (
    <div className="safe-top flex flex-col h-dvh bg-neutral-950">
      <header className="flex items-center gap-3 p-3 border-b border-neutral-800">
        <Link to={backHref} className="text-neutral-400 hover:text-neutral-200">
          <ChevronLeft className="w-5 h-5" />
        </Link>
        <div className="flex-1 min-w-0">
          {session?.project_name && (
            <div className="text-[11px] uppercase tracking-wide font-semibold text-amber-300/90 truncate">
              {session.project_name}
            </div>
          )}
          {titleIsLong ? (
            <button
              type="button"
              onClick={() => setTitleExpanded((v) => !v)}
              className="block w-full text-left font-medium md:text-lg cursor-pointer"
              title={titleExpanded ? 'Свернуть' : 'Раскрыть заголовок'}
            >
              <span
                className={titleExpanded ? 'whitespace-pre-wrap break-words' : 'truncate block'}
              >
                {title}
              </span>
            </button>
          ) : (
            <div className="truncate font-medium md:text-lg">{title}</div>
          )}
          <div className="text-xs text-neutral-500">
            {session && (
              <>
                ${session.cost_usd.toFixed(4)} ·{' '}
                {formatTokens(session.input_tokens)} in /{' '}
                {formatTokens(session.output_tokens)} out
                {session.cache_read_input_tokens > 0 && (
                  <span className="text-emerald-400/70">
                    {' '}({formatTokens(session.cache_read_input_tokens)} cache)
                  </span>
                )}
              </>
            )}
          </div>
        </div>
        {session && <StatusBadge status={session.status} />}
        <button
          type="button"
          onClick={toggleOnlyMine}
          aria-pressed={onlyMine}
          title={onlyMine ? 'Показать все сообщения' : 'Показать только мои сообщения'}
          className={`text-[11px] px-2 py-1 rounded-full border transition ${
            onlyMine
              ? 'border-blue-400/60 bg-blue-500/15 text-blue-100'
              : 'border-neutral-700 bg-neutral-900 text-neutral-400 hover:text-neutral-200'
          }`}
        >
          {onlyMine ? 'только мои' : 'все'}
        </button>
        {canForceFinish && (
          <button
            type="button"
            onClick={finishArmed ? confirmFinish : armFinish}
            title={session?.status === 'error' ? 'Закрыть сессию' : 'Завершить сессию'}
            className={`text-[11px] underline-offset-2 hover:underline ${
              finishArmed
                ? 'text-red-300 font-medium'
                : 'text-neutral-500 hover:text-red-300'
            }`}
          >
            {finishArmed
              ? 'точно?'
              : session?.status === 'error'
                ? 'закрыть'
                : 'завершить'}
          </button>
        )}
      </header>

      {/* Лента инвертирована: свежие события сверху. Сначала идут pending
          permission'ы и финальная плашка (актуальное), потом события от
          новых к старым. */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-2">
        {isFinal && (
          <FinalBanner
            status={session.status}
            costUsd={session.cost_usd}
            inputTokens={session.input_tokens}
            outputTokens={session.output_tokens}
            cacheReadTokens={session.cache_read_input_tokens}
          />
        )}
        {session?.status === 'running' && <TypingIndicator />}
        {permissions.map((p) => (
          <PermissionCard
            key={p.id}
            request={p}
            onApprove={() => void decide(p.id, 'approve')}
            onDeny={() => void decide(p.id, 'deny')}
            onAnswer={(text) => void answer(p.id, text)}
          />
        ))}
        {[...events].reverse().map((ev) => (
          <EventItem key={`${ev.seq}-${ev.id}`} event={ev} />
        ))}
        {hasMoreOlder && (
          <div className="flex justify-center py-3">
            <button
              type="button"
              onClick={() => void loadOlder()}
              disabled={loadingOlder}
              className="text-xs px-3 py-1.5 rounded-full border border-neutral-700 bg-neutral-900 hover:bg-neutral-800 text-neutral-300 disabled:opacity-50"
            >
              {loadingOlder ? 'Загружаю…' : 'Загрузить старее'}
            </button>
          </div>
        )}
      </div>

      {isFinal && !canResume ? (
        <div
          className="border-t border-neutral-800 p-3 text-center text-sm text-neutral-500"
          style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))' }}
        >
          Сессия не возобновляема — начните новую
        </div>
      ) : (
        <div
          ref={inputAreaRef}
          className="border-t border-neutral-800 p-2 flex flex-col gap-2 relative"
          style={{ paddingBottom: 'calc(0.5rem + env(safe-area-inset-bottom))' }}
        >
          {/* Превью прикреплённых изображений */}
          {images.length > 0 && (
            <div className="flex gap-2 flex-wrap px-1">
              {images.map((img) => (
                <div key={img.id} className="relative group">
                  <img
                    src={img.previewUrl}
                    alt={img.filename}
                    className="w-14 h-14 object-cover rounded-lg border border-neutral-700"
                  />
                  <button
                    type="button"
                    onClick={() => setImages((prev) => prev.filter((i) => i.id !== img.id))}
                    className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-600 text-white flex items-center justify-center opacity-80 hover:opacity-100"
                    aria-label={`Удалить ${img.filename}`}
                  >
                    <span className="text-xs leading-none">×</span>
                  </button>
                </div>
              ))}
            </div>
          )}
          {/* Счётчик длины промпта — показываем только в опасной зоне (≥80%
              от лимита), чтобы не отвлекать в обычной переписке. */}
          {prompt.length >= MAX_PROMPT_LENGTH * 0.8 && (
            <div
              className={`px-1 text-xs text-right tabular-nums ${
                prompt.length > MAX_PROMPT_LENGTH ? 'text-red-400' : 'text-neutral-500'
              }`}
            >
              {prompt.length.toLocaleString('ru-RU')} / {MAX_PROMPT_LENGTH.toLocaleString('ru-RU')}
            </div>
          )}
          <div className="flex gap-2 items-end">
            <ImageUploader
              images={images}
              onChange={setImages}
              disabled={!canSend || sending}
              dropZoneRef={inputAreaRef}
            />
            <textarea
              ref={promptRef}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={(e) => {
                if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                  e.preventDefault();
                  void sendPrompt();
                }
              }}
              placeholder={placeholder}
              rows={1}
              disabled={!canSend}
              className="flex-1 min-h-11 max-h-40 px-3 py-2 rounded-xl bg-neutral-900 border border-neutral-800 text-sm md:text-base resize-none font-mono overflow-y-auto"
            />
            {isRunning ? (
              <Button variant="danger" size="md" onClick={() => void interrupt()} aria-label="stop">
                <Square className="w-4 h-4" />
              </Button>
            ) : (
              <Button
                onClick={() => void sendPrompt()}
                disabled={sending || !canSend || !prompt.trim() || prompt.length > MAX_PROMPT_LENGTH}
                aria-label="send"
              >
                <Send className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function TypingIndicator() {
  return (
    <div
      className="self-start flex items-center gap-2 rounded-2xl border border-blue-500/30 bg-blue-500/10 px-3 py-2 text-sm md:text-base 2xl:text-lg text-blue-100 max-w-[85%]"
      role="status"
      aria-label="Агент работает"
    >
      <span className="flex gap-1" aria-hidden>
        <span className="w-1.5 h-1.5 rounded-full bg-blue-300 animate-bounce [animation-delay:-0.32s]" />
        <span className="w-1.5 h-1.5 rounded-full bg-blue-300 animate-bounce [animation-delay:-0.16s]" />
        <span className="w-1.5 h-1.5 rounded-full bg-blue-300 animate-bounce" />
      </span>
      <span className="opacity-90">агент работает…</span>
    </div>
  );
}

function FinalBanner({
  status,
  costUsd,
  inputTokens,
  outputTokens,
  cacheReadTokens,
}: {
  status: SessionStatus;
  costUsd: number;
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
}) {
  const isError = status === 'error';
  const colors = isError
    ? 'border-red-500/40 bg-red-950/30 text-red-100'
    : 'border-emerald-500/30 bg-emerald-950/20 text-emerald-100';
  const icon = isError ? '⚠' : '✅';
  const title = isError ? 'Сессия завершилась с ошибкой' : 'Сессия завершена';
  const totalTokens = inputTokens + outputTokens;
  return (
    <div className={`rounded-2xl border p-4 text-center self-stretch ${colors}`}>
      <div className="text-base font-medium">
        {icon} {title}
      </div>
      {(costUsd > 0 || totalTokens > 0) && (
        <div className="text-xs opacity-70 mt-1">
          {costUsd > 0 && `$${costUsd.toFixed(4)}`}
          {costUsd > 0 && totalTokens > 0 && ' · '}
          {totalTokens > 0 && (
            <>
              {formatTokens(inputTokens)} in / {formatTokens(outputTokens)} out
              {cacheReadTokens > 0 && (
                <span className="text-emerald-300/80"> ({formatTokens(cacheReadTokens)} cache)</span>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function EventItem({ event }: { event: EventRow }) {
  const parsed = safeParse(event.content);

  if (event.type === 'user_prompt') {
    return <UserPromptItem parsed={parsed} failed={event.id.startsWith('failed-')} />;
  }
  if (event.type === 'assistant_text') {
    return <AssistantItem parsed={parsed} />;
  }
  if (event.type === 'tool_result') {
    return <ToolResultItem parsed={parsed} />;
  }
  return <SystemItem parsed={parsed} type={event.type} />;
}

function UserPromptItem({ parsed, failed }: { parsed: unknown; failed?: boolean }) {
  const text = extractUserText(parsed);
  const lineCount = text ? text.split('\n').length : 0;
  const isLong = text.length > 400 || lineCount > 8;
  const [expanded, setExpanded] = useState(false);
  const display = !isLong || expanded
    ? text
    : text.split('\n').slice(0, 8).join('\n');
  const bgClass = failed
    ? 'bg-red-700/80 border border-red-400/50'
    : 'bg-blue-600';
  return (
    <div
      className={`rounded-2xl px-3 py-2 max-w-[85%] text-sm md:text-base 2xl:text-lg self-end text-white ml-auto break-words ${bgClass}`}
    >
      {failed && (
        <div className="text-[11px] text-red-100 mb-1 opacity-90">
          ⚠ не доставлено
        </div>
      )}
      <div
        className="whitespace-pre-wrap"
        style={{ overflowWrap: 'anywhere' }}
      >
        {display || '(user message)'}
        {isLong && !expanded && <span className="opacity-60">…</span>}
      </div>
      {isLong && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-1 text-[11px] text-blue-100 underline-offset-2 hover:underline opacity-90"
        >
          {expanded ? 'Свернуть' : 'Развернуть'}
        </button>
      )}
    </div>
  );
}

function AssistantItem({ parsed }: { parsed: unknown }) {
  // AskUserQuestion отображается отдельной интерактивной карточкой над лентой
  // (AskUserQuestionCard). Дублировать ту же информацию read-only-блоком в
  // сообщении ассистента не нужно — после ответа tool_result сам сохраняет
  // Q+A для истории (см. AskUserQuestionCard.submit).
  const blocks = extractAssistantBlocks(parsed).filter(
    (b) => !(b.kind === 'tool_use' && b.name === 'AskUserQuestion'),
  );
  if (blocks.length === 0) {
    // Сообщение содержало только thinking-блоки или один отфильтрованный
    // AskUserQuestion. Раньше показывали пустую плашку «(assistant)» —
    // выглядело сломанно, особенно когда из-за этого казалось, что
    // вопросов нет вообще. Не рендерим ничего: вопрос виден через
    // карточку permission'а сверху, остальное — внутреннее.
    return null;
  }
  return (
    <div className="space-y-2 max-w-[85%] self-start">
      {blocks.map((b, i) => {
        if (b.kind === 'text') {
          return (
            <div
              key={i}
              className="rounded-2xl px-3 py-2 bg-neutral-800 text-neutral-100 text-sm md:text-base 2xl:text-lg break-words"
            >
              <Markdown>{b.text}</Markdown>
            </div>
          );
        }
        return (
          <div
            key={i}
            className="rounded-2xl border border-neutral-800 bg-neutral-900 p-3 space-y-2"
          >
            <div className="text-[13px] font-medium text-indigo-300">🔧 {b.name}</div>
            <ToolInputView toolName={b.name} toolInput={JSON.stringify(b.input)} />
          </div>
        );
      })}
    </div>
  );
}

function ToolResultItem({ parsed }: { parsed: unknown }) {
  const { text, isError } = extractToolResult(parsed);
  const cleaned = stripAnsi(stripSystemReminders(text));
  const border = isError ? 'border-red-500/40 bg-red-950/20' : 'border-neutral-800 bg-neutral-900';
  const label = isError ? '⚠ результат с ошибкой' : '✓ результат';
  return (
    <div className={`rounded-2xl border ${border} p-2 max-w-[85%] self-start`}>
      <CollapsibleBlock label={label} previewLines={5}>
        <ResultBody text={cleaned} />
      </CollapsibleBlock>
    </div>
  );
}

function ResultBody({ text }: { text: string }) {
  if (!text) {
    return <div className="text-[12px] text-neutral-500 italic">(пусто)</div>;
  }
  const lines = text.split('\n');
  // Read и Edit-результат возвращают `cat -n`-формат, где между номером и
  // текстом — либо `\t`, либо unicode-стрелка `→` (U+2192). Поддерживаем оба.
  const isLineNumbered = lines.length > 1 && lines.slice(0, 5).filter(hasLineNumberPrefix).length >= 2;
  if (isLineNumbered) {
    return (
      <div className="font-mono text-[12px] grid grid-cols-[auto_1fr] gap-x-3">
        {lines.map((line, i) => {
          const m = line.match(LINE_PREFIX_RE);
          if (!m) {
            return (
              <div key={i} className="col-span-2 text-neutral-200 break-words whitespace-pre-wrap">
                {line || ' '}
              </div>
            );
          }
          return (
            <div key={i} className="contents">
              <div className="text-neutral-500 text-right tabular-nums select-none">{m[2]}</div>
              <div className="text-neutral-200 break-words whitespace-pre-wrap">{m[3] || ' '}</div>
            </div>
          );
        })}
      </div>
    );
  }
  return (
    <pre className="font-mono text-[12px] text-neutral-200 whitespace-pre-wrap break-words">
      {text}
    </pre>
  );
}

const LINE_PREFIX_RE = /^(\s*)(\d+)(?:\t|→)(.*)$/;

function hasLineNumberPrefix(line: string): boolean {
  return LINE_PREFIX_RE.test(line);
}

function stripSystemReminders(text: string): string {
  return text.replace(/<system-reminder>[\s\S]*?<\/system-reminder>\s*/g, '').trim();
}

// ANSI escape-коды (composer/npm/pnpm/git и т.п. подсвечивают вывод цветами).
// В <pre> они показываются как сырые `[90m`/`[39m` — портят читабельность.
// Снимаем CSI и OSC; цвета теряем, но логи остаются разборчивыми.
// eslint-disable-next-line no-control-regex
const ANSI_CSI_RE = /\x1B\[[0-9;?]*[A-Za-z]/g;
// eslint-disable-next-line no-control-regex
const ANSI_OSC_RE = /\x1B\][^\x07]*(?:\x07|\x1B\\)/g;

function stripAnsi(text: string): string {
  return text.replace(ANSI_CSI_RE, '').replace(ANSI_OSC_RE, '');
}

function SystemItem({ parsed, type }: { parsed: unknown; type: SessionEventType }) {
  const text = summarizeSystem(parsed, type);
  return (
    <div className="rounded-xl px-3 py-1.5 self-stretch bg-neutral-900/50 text-neutral-500 text-[11px] text-center">
      {text}
    </div>
  );
}

function CollapsibleBlock({
  label,
  previewLines,
  children,
}: {
  label: string;
  previewLines: number;
  children: React.ReactNode;
}) {
  const [expanded, setExpanded] = useState(false);
  const maxHeight = `${previewLines * 1.2}rem`;
  return (
    <div className="space-y-1.5">
      <div className="text-[11px] text-neutral-400">{label}</div>
      <div
        className="overflow-hidden relative"
        style={expanded ? { maxHeight: '60vh', overflow: 'auto' } : { maxHeight }}
      >
        {children}
        {!expanded && (
          <div className="absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-neutral-900 to-transparent pointer-events-none" />
        )}
      </div>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="text-[11px] text-neutral-400 hover:text-neutral-200 inline-flex items-center gap-1"
      >
        {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
        {expanded ? 'Свернуть' : 'Раскрыть'}
      </button>
    </div>
  );
}

type AssistantBlockTyped =
  | { kind: 'text'; text: string }
  | { kind: 'tool_use'; name: string; input: unknown };

function extractAssistantBlocks(parsed: unknown): AssistantBlockTyped[] {
  if (!parsed || typeof parsed !== 'object' || !('message' in parsed)) return [];
  const m = (parsed as { message?: { content?: unknown } }).message;
  if (!m || !Array.isArray(m.content)) return [];
  const out: AssistantBlockTyped[] = [];
  for (const c of m.content) {
    if (typeof c !== 'object' || c === null || !('type' in c)) continue;
    const block = c as { type?: string; text?: string; name?: string; input?: unknown };
    if (block.type === 'text' && typeof block.text === 'string' && block.text.length > 0) {
      out.push({ kind: 'text', text: block.text });
    } else if (block.type === 'tool_use' && typeof block.name === 'string') {
      out.push({ kind: 'tool_use', name: block.name, input: block.input ?? {} });
    }
  }
  return out;
}

function extractUserText(parsed: unknown): string {
  if (!parsed || typeof parsed !== 'object' || !('message' in parsed)) return '';
  const m = (parsed as { message?: { content?: unknown } }).message;
  if (!m) return '';
  if (typeof m.content === 'string') return m.content;
  if (Array.isArray(m.content)) {
    return m.content
      .map((c) => (typeof c === 'object' && c && 'text' in c ? String((c as { text: unknown }).text) : ''))
      .join('');
  }
  return '';
}

function extractToolResult(parsed: unknown): { text: string; isError: boolean } {
  if (!parsed || typeof parsed !== 'object') return { text: '', isError: false };
  const obj = parsed as Record<string, unknown>;

  if ('message' in obj) {
    const m = obj.message as { content?: unknown } | undefined;
    if (m && Array.isArray(m.content)) {
      for (const c of m.content) {
        if (typeof c === 'object' && c && 'type' in c && (c as { type: unknown }).type === 'tool_result') {
          const block = c as { content?: unknown; is_error?: boolean };
          return { text: stringifyContent(block.content), isError: !!block.is_error };
        }
      }
    }
  }

  if ('tool_use_result' in obj) {
    return { text: stringifyContent(obj.tool_use_result), isError: false };
  }

  return { text: '', isError: false };
}

function stringifyContent(content: unknown): string {
  if (typeof content === 'string') return content;
  if (Array.isArray(content)) {
    return content
      .map((c) => {
        if (typeof c === 'string') return c;
        if (typeof c === 'object' && c && 'text' in c) return String((c as { text: unknown }).text);
        return JSON.stringify(c);
      })
      .join('\n');
  }
  if (content === null || content === undefined) return '';
  if (typeof content === 'object') return JSON.stringify(content, null, 2);
  return String(content);
}

function summarizeSystem(parsed: unknown, type: SessionEventType): string {
  if (parsed && typeof parsed === 'object') {
    if ('subtype' in parsed && (parsed as { subtype?: string }).subtype === 'init') {
      return '✳ сессия запущена';
    }
    if ('type' in parsed && (parsed as { type?: string }).type === 'result') {
      const obj = parsed as { total_cost_usd?: number };
      const cost = typeof obj.total_cost_usd === 'number' ? ` · $${obj.total_cost_usd.toFixed(4)}` : '';
      return `✅ ход завершён${cost}`;
    }
  }
  return `(${type})`;
}

function PermissionCard({
  request,
  onApprove,
  onDeny,
  onAnswer,
}: {
  request: PermissionRequest;
  onApprove: () => void;
  onDeny: () => void;
  onAnswer: (text: string) => void;
}) {
  if (request.toolName === 'AskUserQuestion') {
    return (
      <AskUserQuestionCard request={request} onAnswer={onAnswer} onDeny={onDeny} />
    );
  }
  return (
    <div className="rounded-2xl border border-amber-600/40 bg-amber-500/10 p-3 space-y-2">
      <div className="text-sm font-medium">🔧 {request.toolName} — нужно разрешение</div>
      <ToolInputView toolName={request.toolName} toolInput={request.toolInput} />
      <div className="flex gap-2">
        <Button variant="danger" className="flex-1" onClick={onDeny}>
          Deny
        </Button>
        <Button className="flex-1" onClick={onApprove}>
          Approve
        </Button>
      </div>
    </div>
  );
}

function safeParse(json: string): unknown {
  try {
    return JSON.parse(json);
  } catch {
    return json;
  }
}

